// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import {
  DocumentBusinessType,
  DocumentMetadata,
  ShareholderData,
  Step,
  UploadedFile,
} from "../../types";

// UTILS
import { input } from "../../inputs";
import { filesNotRequired, filesRequired } from "../../validations";
import {
  mapDocumentBusinessType,
  replaceSpecialCharacters,
  translateCorporate,
} from "../../app/utils";
import {
  juridicalDocumentLinks,
  maxFileSize,
  validBusinessDocumentationTypes,
} from "../../utils";
import {
  deleteBusinessDocument,
  getBusinessDocuments,
  sendBusinessDocument,
} from "../../connectors/connectors";
import { onUpdateCompletedStep } from "../../reducers/onboardingDataReducer";

const juridicalDocumentationStep = (
  businessDocumentsByType: Record<string, DocumentMetadata[]> | null,
): Omit<Step, "step"> => {
  return {
    name: "Documentación",
    subtitle: "Cargá archivos PDF o DOC de hasta 10mb.",
    fields: [
      input("document", {
        name: "businessDocumentation.statute",
        label: "Estatuto o contrato social escaneado",
        conditional: {
          name: "businessInformation.corporateType",
          value: [
            translateCorporate.associationFoundationCooperativeChamber,
            translateCorporate.governmentAgencyOrEntity,
            translateCorporate.saCorporation,
            translateCorporate.simplifiedJointStockCompany,
            translateCorporate.reciprocalGuaranteeCompany,
            translateCorporate.llcLimitedLiabilityCompany,
            translateCorporate.other,
          ],
        },
      }),
      input("document", {
        name: "businessDocumentation.horizontalPropertyRegulationsScanned",
        label: "Reglamento de propiedad horizontal escaneado",
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.consortium],
        },
      }),
      input("document", {
        name: "businessDocumentation.trustConstitutionContract",
        label: "Contrato de constitución del Fideicomiso",
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.trust],
        },
      }),
      input("document", {
        name: "businessDocumentation.poa",
        label: "Poder de los firmantes",
        validation: filesNotRequired(
          validBusinessDocumentationTypes,
          maxFileSize,
        ),
        conditional: {
          name: "businessInformation.corporateType",
          value: [
            translateCorporate.saCorporation,
            translateCorporate.llcLimitedLiabilityCompany,
            translateCorporate.associationFoundationCooperativeChamber,
            translateCorporate.trust,
            translateCorporate.simplifiedJointStockCompany,
            translateCorporate.reciprocalGuaranteeCompany,
            translateCorporate.consortium,
            translateCorporate.governmentAgencyOrEntity,
          ],
        },
        tooltip: {
          content: "En caso de corresponder",
        },
      }),
      input("document", {
        name: "businessDocumentation.balance",
        label: "Balance certificado por CPCE o doc. patrimonial",
        conditional: {
          name: "businessInformation.corporateType",
          value: [
            translateCorporate.saCorporation,
            translateCorporate.llcLimitedLiabilityCompany,
            translateCorporate.associationFoundationCooperativeChamber,
            translateCorporate.trust,
            translateCorporate.simplifiedJointStockCompany,
            translateCorporate.reciprocalGuaranteeCompany,
            translateCorporate.governmentAgencyOrEntity,
            translateCorporate.consortium,
          ],
        },
        tooltip: {
          content:
            "Si no tenes el último balance, asegurate de incluir documentación patrimonial actualizada que demuestre el origen de los fondos a ingresar (por ejemplo: certificación contable o facturas). También podes adjuntar voluntariamente  declaraciones juradas IVA como un respaldo adicional.",
        },
      }),
      input("document", {
        name: "businessDocumentation.sharesRegister",
        label: "Registro de accionistas",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.saCorporation,
        },
      }),
      input("document", {
        name: "businessDocumentation.administratorDesignationConsortium",
        label: "Acta de designación de administrador",
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.consortium],
        },
      }),
      input("document", {
        name: "businessDocumentation.administratorDesignationSas",
        label: "Acta de designación de administrador",
        validation: filesNotRequired(
          validBusinessDocumentationTypes,
          maxFileSize,
        ),
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.simplifiedJointStockCompany],
        },
      }),
      input("document", {
        name: "businessDocumentation.expenseLiquidation",
        label: "Liquidación de expensas",
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.consortium],
        },
      }),
      input("document", {
        name: "businessDocumentation.authorities",
        label: "Acta de designación de autoridades",
        conditional: {
          name: "businessInformation.corporateType",
          value: [
            translateCorporate.saCorporation,
            translateCorporate.associationFoundationCooperativeChamber,
            translateCorporate.reciprocalGuaranteeCompany,
            translateCorporate.governmentAgencyOrEntity,
            translateCorporate.other,
          ],
        },
      }),
      input("document", {
        name: "businessDocumentation.uifInscriptionCertificate",
        label: "Constancia de inscripción ante UIF del fiduciario",
        validation: filesNotRequired(
          validBusinessDocumentationTypes,
          maxFileSize,
        ),
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.trust,
        },
      }),
      input("document", {
        name: "businessDocumentation.worthStatement",
        label: "Documentación patrimonial",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.other,
        },
      }),
      input("document", {
        name: "businessDocumentation.partnersNote",
        label: "Nota de socios protectores y beneficiarios",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.reciprocalGuaranteeCompany,
        },
        tooltip: {
          content:
            'Vamos a necesitar una nota detallada con los datos de los socios protectores. Descargate la nota "ejemplo" para completar la misma',
        },
        anchor: {
          text: "Ejemplo",
          onClick() {
            const fileUrl =
              juridicalDocumentLinks["businessDocumentation.partnersNote"];
            const link = document.createElement("a");
            link.href = fileUrl;
            link.download = "Nota de socios protectores y beneficiarios.docx";
            link.click();
          },
        },
      }),
      input("document", {
        name: "businessDocumentation.partnersList",
        label: "Nómina de socios",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.reciprocalGuaranteeCompany,
        },
      }),
      input("document", {
        name: "businessDocumentation.membersNote",
        label: "Nota de miembros",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.associationFoundationCooperativeChamber,
        },
        tooltip: {
          content:
            'Vamos a necesitar una nota detallada donde se indique la composición de la fundación, junto con los datos de los miembros del órgano de administración. Descargate la nota "ejemplo" para completar la misma.',
        },
        anchor: {
          text: "Ejemplo",
          onClick() {
            const fileUrl =
              juridicalDocumentLinks["businessDocumentation.membersNote"];
            const link = document.createElement("a");
            link.href = fileUrl;
            link.download = "Nota de miembros.docx";
            link.click();
          },
        },
      }),
      input("document", {
        name: "businessDocumentation.trustNote",
        label: "Nota de fiduciantes, fiduciarios y beneficiarios",
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.trust,
        },
        tooltip: {
          content:
            'Vamos a necesitar una nota detallada con los datos de todos los fiduciantes, beneficiarios y fiduciario del fideicomiso. Descargate la nota "ejemplo" para completar la misma.',
        },
        anchor: {
          text: "Ejemplo",
          onClick() {
            const fileUrl =
              juridicalDocumentLinks["businessDocumentation.trustNote"];
            const link = document.createElement("a");
            link.href = fileUrl;
            link.download =
              "Nota de fiduciantes, fiduciarios y beneficiarios.docx";
            link.click();
          },
        },
      }),
      input("document", {
        // The dictionary default does not apply: this one validates off state
        validation: undefined,
        name: "businessDocumentation.shareholdersNote",
        label: "Nota de accionistas y beneficiarios finales",
        conditionalValidation: (state) => {
          if (!state) throw new Error("Error: state not found");

          const { formValues } = state.onboardingData;
          const shareholders: ShareholderData[] =
            formValues?.shareholders || [];

          const allPhysicalShareholders = shareholders.every(
            (shareholder) => shareholder?.personType === "PHYSICAL",
          );

          return allPhysicalShareholders
            ? filesNotRequired(validBusinessDocumentationTypes, maxFileSize)
            : filesRequired(validBusinessDocumentationTypes, maxFileSize);
        },
        conditional: {
          name: "businessInformation.corporateType",
          value: [
            translateCorporate.saCorporation,
            translateCorporate.llcLimitedLiabilityCompany,
            translateCorporate.simplifiedJointStockCompany,
            translateCorporate.other,
          ],
        },
        tooltip: {
          content:
            "En caso de tener accionistas personas jurídicas necesitaremos la nota adjunta, indicando y detallando la cadena accionaria hasta identificar a los los beneficiarios finales personas humanas que posean hasta el 10% de participación directa e indirecta.",
        },
        anchor: {
          text: "Ejemplo",
          onClick() {
            const fileUrl =
              juridicalDocumentLinks["businessDocumentation.shareholdersNote"];
            const link = document.createElement("a");
            link.href = fileUrl;
            link.download = "Nota de accionistas y beneficiarios finales.docx";
            link.click();
          },
        },
      }),
      input("document", {
        name: "businessDocumentation.coownersList",
        label: "Nómina de copropietarios",
        conditional: {
          name: "businessInformation.corporateType",
          value: [translateCorporate.consortium],
        },
      }),
      input("document", {
        name: "businessDocumentation.other",
        label: "Otros",
        validation: filesNotRequired(
          validBusinessDocumentationTypes,
          maxFileSize,
        ),
        conditionalTooltip: [
          {
            name: "businessInformation.corporateType",
            value: [
              translateCorporate.saCorporation,
              translateCorporate.associationFoundationCooperativeChamber,
              translateCorporate.simplifiedJointStockCompany,
              translateCorporate.reciprocalGuaranteeCompany,
              translateCorporate.governmentAgencyOrEntity,
              translateCorporate.other,
              translateCorporate.trust,
            ],
            tooltip: {
              content:
                "Podes adjuntar cualquier documento adicional que nos permita realizar un análisis completo y preciso de tu sociedad. Esto nos ayudará a procesar tu solicitud de manera más eficiente",
            },
          },
          {
            name: "businessInformation.corporateType",
            value: [translateCorporate.llcLimitedLiabilityCompany],
            tooltip: {
              content:
                "Podes adjuntar cualquier documento adicional, como, por ejemplo: cesiones de cuotas posteriores o modificaciones en la composición de la gerencia. Esto nos ayudará a procesar tu solicitud de manera más eficiente",
            },
          },
        ],
      }),
    ],
    fieldsSections: [
      {
        name: "Completar sólo si el Fiduciario es una Persona Jurídica:",
        fields: [
          input("document", {
            name: "businessDocumentation.trusteeDocumentationStatute",
            label: "Estatuto o contrato social",
            validation: filesNotRequired(
              validBusinessDocumentationTypes,
              maxFileSize,
            ),
            conditional: {
              name: "businessInformation.corporateType",
              value: translateCorporate.trust,
            },
          }),
          input("document", {
            name: "businessDocumentation.trusteeDocumentationAuthorities",
            label: "Acta con designación de autoridades vigentes",
            validation: filesNotRequired(
              validBusinessDocumentationTypes,
              maxFileSize,
            ),
            conditional: {
              name: "businessInformation.corporateType",
              value: translateCorporate.trust,
            },
          }),
        ],
        conditional: {
          name: "businessInformation.corporateType",
          value: translateCorporate.trust,
        },
      },
    ],
    onContinue: async (
      values: FormikValues,
      dispatch,
      state,
      setFieldValue,
    ) => {
      if (!state || !dispatch)
        throw new Error("Error: state or dispatch not found");

      const {
        onboardingData: { draftUUID },
      } = state;

      const { businessDocumentation } = values;
      const safeBusinessDocumentation =
        (businessDocumentation as Record<string, unknown>) || {};

      const uploadPromises: Promise<void>[] = [];
      const deletePromises: Promise<void>[] = [];

      const remoteDocuments = draftUUID
        ? ((await getBusinessDocuments({ draftUUID }).catch(() => null))
            ?.data ?? [])
        : [];

      for (const key in safeBusinessDocumentation) {
        const documentType = mapDocumentBusinessType(
          key,
        ) as DocumentBusinessType;
        const files = Array.isArray(safeBusinessDocumentation[key])
          ? (safeBusinessDocumentation[key] as UploadedFile[])
          : [];

        for (const file of files) {
          const businessFile = file as UploadedFile;
          // Upload if its a File and is not uploaded yet
          if (businessFile instanceof File && !businessFile.__alreadyUploaded) {
            const alreadySent = remoteDocuments.some(
              (doc) =>
                doc.type === documentType &&
                doc.originalFileName ===
                  replaceSpecialCharacters(businessFile.name),
            );
            if (alreadySent) {
              businessFile.__alreadyUploaded = true;
              continue;
            }

            const promise = (async () => {
              await sendBusinessDocument({
                draftUUID,
                documentType,
                file: businessFile,
              });

              // Mark file as already uploaded to prevent re-uploading
              businessFile.__alreadyUploaded = true;
            })();

            uploadPromises.push(promise);
          }
        }
      }

      for (const key in businessDocumentsByType || {}) {
        const remoteBusinessDocuments = businessDocumentsByType?.[key] || [];
        const documentType = mapDocumentBusinessType(
          key,
        ) as DocumentBusinessType;
        const files = Array.isArray(safeBusinessDocumentation[key])
          ? (safeBusinessDocumentation[key] as UploadedFile[])
          : [];

        const docsToDelete = remoteBusinessDocuments?.filter((doc) => {
          return !files.some(
            (file: UploadedFile) =>
              replaceSpecialCharacters(file.name) === doc.originalFileName,
          );
        });

        if (docsToDelete) {
          const promise = (async () => {
            for (const docToDelete of docsToDelete)
              await deleteBusinessDocument({
                draftUUID,
                documentType,
                documentUUID: docToDelete.uuid,
              });
          })();

          setFieldValue && setFieldValue(`businessDocumentation.${key}`, files);
          deletePromises.push(promise);
        }
      }

      await Promise.all(uploadPromises);
      await Promise.all(deletePromises);

      dispatch(onUpdateCompletedStep(8));
    },
  };
};

export default juridicalDocumentationStep;
